import { createInterface, type Interface } from 'node:readline';
import {
  createAiClient,
  type AiUsage,
  type ChatMessage,
  type ToolCall,
  type ToolDefinition,
} from '@fim/ai-sdk';
import { createToolRegistry, type ToolContext } from '@fim/futurex-tools';
import type { Message } from '@fim/futurex-protocol';
import { augmentMessages } from './augment.js';
import {
  classifyVerifyNeed,
  createChangeTracker,
  mergeChange,
  type ChangedFile,
  type ChangeSet,
} from './change-significance.js';
import { measureFileChange } from './churn.js';
import { loadContext, type SessionContext } from './context.js';
import { readCreds } from './creds.js';
import { createGatewayClient } from './client/gateway.js';
import { createPermissionGate, type AskFn, type Gate } from './permissions.js';
import { accumulateUsage } from './pricing.js';
import { findRepoRoot } from './repo.js';
import {
  createDoneFilter,
  decideNext,
  NUDGE_FINISH,
  NUDGE_LENGTH,
  nudgeVerifyFailures,
  runGatewayTurnToCompletion,
  runVerifyPhase,
  type VerifyMode,
  type VerifyOutcome,
} from './finish.js';
import { AGENT_PROTOCOL } from './system-prompt.js';
import type { CliOptions } from './options.js';
import {
  color,
  errColor,
  eout,
  error,
  info,
  line,
  out,
  phase,
  startSpinner,
  toolCallLine,
  toolResultLine,
  verdictLine,
  type Spinner,
} from './ui.js';

export interface AgentOptions {
  /** Approve every mutating tool without prompting (dangerous; for scripts/tests). */
  readonly autoApprove: boolean;
  /** Max model↔tool rounds before stopping. */
  readonly maxSteps: number;
  /** Run the repo's checks after the agent writes files ('auto', default) or not ('off'). */
  readonly verify?: VerifyMode;
}

export type { Gate } from './permissions.js';

function question(rl: Interface, q: string): Promise<string> {
  return new Promise((resolve) => rl.question(q, resolve));
}

/**
 * A permission prompt over readline (one-shot `futurex agent` mode). Always-*
 * answers are persisted per action kind by the permission gate wrapping this.
 */
export function createReadlineAsk(rl: Interface): AskFn {
  return async (req) => {
    // Non-interactive stdin can never answer the prompt → fail safe (deny), don't hang.
    if (process.stdin.isTTY !== true) {
      info('non-interactive stdin: re-run with --auto-approve to permit writes/exec');
      return 'no';
    }
    const caution = req.kind === 'run' ? ' (commands run with your shell access)' : '';
    eout(`\n${errColor.yellow('permission')} [${req.kind}] ${req.preview}${caution}\n`);
    const ans = (
      await question(
        rl,
        errColor.yellow(`  approve? [y]es / [n]o / [a]lways allow ${req.kind} / always [d]eny ${req.kind} `),
      )
    )
      .trim()
      .toLowerCase();
    if (ans === 'a' || ans === 'always') return 'always-allow';
    if (ans === 'd' || ans === 'deny' || ans === 'never') return 'always-deny';
    if (ans === 'y' || ans === 'yes') return 'yes';
    return 'no';
  };
}

/** Dependencies of one agent turn (exported for the orchestrator tests). */
export interface TurnDeps {
  readonly ai: ReturnType<typeof createAiClient>;
  readonly toolDefs: ToolDefinition[];
  readonly registry: ReturnType<typeof createToolRegistry>;
  readonly toolCtx: ToolContext;
  readonly agentSystem: string;
  readonly model: string;
  readonly maxTokens?: number;
  readonly maxSteps: number;
  readonly gate: Gate;
  readonly signal: AbortSignal;
  /** Repo root, for the automatic verification phase. */
  readonly root: string;
  readonly verify: VerifyMode;
}

/**
 * One user turn of the agent as a phased pipeline over a PERSISTENT `messages`
 * array: PLAN/BUILD — stream the model, run its tool calls (reads auto,
 * writes/exec gated) and feed results back; VERIFY — when files changed, run
 * the repo's own checks; REPAIR — feed failures back for bounded fix rounds.
 *
 * The turn only ends when the completion policy (finish.ts) accepts it: a
 * length-cut answer is auto-continued, a "narrate and stop" answer is nudged
 * to keep working, and written files must pass the project checks. The user
 * never has to type "continue".
 */
export async function runAgentTurn(messages: ChatMessage[], d: TurnDeps): Promise<AiUsage | undefined> {
  // Accumulated across ALL steps of the turn — budgets/billing must see every
  // model call of the tool loop, not just the final one.
  let turnUsage: AiUsage | undefined;
  const startedAt = Date.now();
  let toolRuns = 0;
  const filesChanged = new Set<string>();
  let shownPhase: string | null = null;
  let verifyMode = d.verify;
  let lengthContinues = 0;
  let finishNudges = 0;
  let repairRounds = 0;
  let mutatedSinceVerify = false;
  let verifyRan = false;
  let verifyFailed = false;
  // Sticky: once a check has failed this turn, every later change is re-verified
  // (the model's trivial waiver can't be trusted once the build is known broken).
  let everFailedVerify = false;
  let lastVerify: VerifyOutcome | null = null;
  // Pending (unverified) changes, for the verification-need gate. Reset whenever
  // verification runs — the records left after that are post-verify edits.
  const pendingChanges = new Map<string, ChangedFile>();
  let opaqueMutation = false;

  const showPhase = (p: 'build' | 'verify' | 'repair'): void => {
    if (shownPhase !== p) {
      shownPhase = p;
      phase(p);
    }
  };

  const finishTurn = (): AiUsage | undefined => {
    if (toolRuns > 0) {
      const secs = ((Date.now() - startedAt) / 1000).toFixed(1);
      const bits = [`${secs}s`, `${toolRuns} tool call${toolRuns === 1 ? '' : 's'}`];
      if (filesChanged.size > 0) bits.push(`${filesChanged.size} file${filesChanged.size === 1 ? '' : 's'} changed`);
      if (verifyRan && !verifyFailed) bits.push('checks pass');
      const mark = verifyFailed ? color.yellow('!') : color.green('✓');
      line(`${mark} ${color.dim(bits.join(' · '))}`);
      if (verifyFailed && lastVerify) {
        for (const l of lastVerify.lines) {
          if (l.includes('FAIL')) line(verdictLine(false, l));
        }
        info('verification failures remain — say "fix the failing checks" to retry');
      }
    }
    return turnUsage;
  };

  const spinLabel = (step: number): string => {
    if (shownPhase === 'repair') return 'repairing…';
    if (step === 0) return messages.length <= 2 ? 'planning…' : 'thinking…';
    return 'working…';
  };

  for (let step = 0; step < d.maxSteps; step++) {
    const lastStep = step === d.maxSteps - 1;
    let text = '';
    let toolCalls: ToolCall[] = [];
    let finishReason: string | undefined;
    let printedText = false;
    const filter = createDoneFilter();
    let spin: Spinner | null = startSpinner(spinLabel(step));
    const stopSpin = (): void => {
      if (spin) {
        spin.stop();
        spin = null;
      }
    };
    try {
      for await (const chunk of d.ai.stream({
        model: d.model,
        ...(d.maxTokens !== undefined ? { maxTokens: d.maxTokens } : {}),
        system: d.agentSystem,
        messages,
        tools: d.toolDefs,
        // Force a final text answer on the last step instead of dead-ending mid-tool.
        toolChoice: lastStep ? 'none' : 'auto',
        reasoning: false,
        signal: d.signal,
      })) {
        stopSpin(); // first chunk arrived — clear the spinner before any output
        if (chunk.delta) {
          const visible = filter.push(chunk.delta);
          if (visible) {
            out(visible);
            text += visible;
            printedText = true;
          }
        }
        if (chunk.done) {
          if (chunk.toolCalls) toolCalls = [...chunk.toolCalls];
          finishReason = chunk.finishReason;
          if (chunk.usage) turnUsage = turnUsage ? accumulateUsage(turnUsage, chunk.usage) : chunk.usage;
        }
      }
    } finally {
      stopSpin();
    }
    const rest = filter.flush();
    if (rest) {
      out(rest);
      text += rest;
      printedText = true;
    }
    if (printedText) out('\n');

    if (d.signal.aborted) {
      info('cancelled');
      return turnUsage;
    }

    if (toolCalls.length > 0) {
      showPhase('build');
      messages.push({ role: 'assistant', content: text, toolCalls });

      for (const tc of toolCalls) {
        const spec = d.registry.get(tc.name);
        if (!spec) {
          messages.push({ role: 'tool', toolCallId: tc.id, content: `unknown tool: ${tc.name}` });
          continue;
        }
        let parsed: unknown;
        try {
          parsed = spec.schema.parse(JSON.parse(tc.arguments || '{}'));
        } catch (err) {
          messages.push({
            role: 'tool',
            toolCallId: tc.id,
            content: `invalid arguments: ${err instanceof Error ? err.message : String(err)}`,
          });
          continue;
        }
        const preview = spec.preview(parsed, d.toolCtx);
        const kind = spec.classify(parsed, d.toolCtx);
        if (kind !== null) {
          const approved = await d.gate({ tool: spec.name, kind, preview });
          if (!approved) {
            line(verdictLine(false, `denied: ${spec.name}`));
            messages.push({ role: 'tool', toolCallId: tc.id, content: 'denied by the user' });
            continue;
          }
        }
        line(toolCallLine(preview, spec.mutating));
        // Measure the file's pre-change state BEFORE the write replaces it, so a
        // trivial edit can be sized for the verification-need gate.
        const measured = spec.mutating
          ? measureFileChange(d.root, spec.name, parsed as { path?: unknown; content?: unknown })
          : null;
        let result: { ok: boolean; output: string };
        try {
          result = await spec.run(parsed, d.toolCtx);
        } catch (err) {
          result = { ok: false, output: `tool error: ${err instanceof Error ? err.message : String(err)}` };
        }
        toolRuns += 1;
        if (spec.mutating && result.ok) {
          mutatedSinceVerify = true;
          verifyRan = false;
          verifyFailed = false;
          if (measured) {
            filesChanged.add(measured.path);
            const key = measured.path.replace(/\\/g, '/');
            pendingChanges.set(key, mergeChange(pendingChanges.get(key), measured));
          } else {
            opaqueMutation = true; // terminal_exec — unknown blast radius
          }
        }
        line(toolResultLine(result.output, result.ok));
        messages.push({ role: 'tool', toolCallId: tc.id, content: result.output });
      }
      continue;
    }

    // ── Text-only stop: apply the completion policy ─────────────────────────
    if (text) messages.push({ role: 'assistant', content: text });

    // Do the pending changes actually warrant the project checks?
    let changesNeedVerify = true;
    if (verifyMode === 'auto' && mutatedSinceVerify && !verifyRan) {
      if (everFailedVerify) {
        // A check already failed this turn — re-verify regardless of the waiver.
        changesNeedVerify = true;
      } else {
        const changeSet: ChangeSet = { files: [...pendingChanges.values()], opaqueMutation };
        const need = classifyVerifyNeed(changeSet, { modelWaived: filter.sawNoVerify });
        changesNeedVerify = need.verify;
        if (!need.verify) info(`skipping verification — ${need.reason}`);
      }
    }

    let proceed = false;
    while (!proceed) {
      const action = decideNext({
        sawMarker: filter.sawMarker,
        finishReason,
        mutatedSinceVerify,
        verifyRan,
        verifyFailed,
        changesNeedVerify,
        lengthContinues,
        finishNudges,
        repairRounds,
        verifyMode,
      });
      switch (action.kind) {
        case 'accept':
          return finishTurn();
        case 'continue-length':
          lengthContinues += 1;
          info('output hit the token limit — continuing automatically');
          messages.push({ role: 'user', content: NUDGE_LENGTH });
          proceed = true;
          break;
        case 'nudge-finish':
          finishNudges += 1;
          messages.push({ role: 'user', content: NUDGE_FINISH });
          proceed = true;
          break;
        case 'repair':
          showPhase('repair');
          repairRounds += 1;
          info(`repair round ${repairRounds} — feeding the failures back`);
          messages.push({
            role: 'user',
            content: nudgeVerifyFailures(lastVerify?.failureSummary ?? 'checks failed'),
          });
          proceed = true;
          break;
        case 'verify': {
          showPhase('verify');
          const vSpin = startSpinner('verifying — running project checks…');
          try {
            lastVerify = await runVerifyPhase(d.root, { signal: d.signal });
          } finally {
            vSpin.stop();
          }
          if (d.signal.aborted) {
            info('cancelled');
            return turnUsage;
          }
          if (!lastVerify.ran) {
            // No checks in this repo — verification can't gate completion here.
            info('no typecheck/lint/test scripts detected — skipping verification');
            verifyMode = 'off';
            verifyRan = false;
            verifyFailed = false;
          } else {
            verifyRan = true;
            verifyFailed = !lastVerify.ok;
            if (verifyFailed) everFailedVerify = true;
            for (const l of lastVerify.lines) line(verdictLine(!l.includes('FAIL'), l));
            if (lastVerify.timedOut) info('a check hit its time budget — result treated as inconclusive');
          }
          // These changes are now verified — any later repair edits start fresh.
          pendingChanges.clear();
          opaqueMutation = false;
          break; // re-decide with the verification outcome
        }
      }
    }
  }
  info(`reached the ${d.maxSteps}-step safety limit — stopping; send another message to continue`);
  return finishTurn();
}

function toolDefsFrom(registry: ReturnType<typeof createToolRegistry>): ToolDefinition[] {
  return registry.specs.map((s) => ({
    name: s.name,
    description: s.description,
    parameters: s.parameters,
  }));
}

/**
 * Local agentic loop for `futurex agent "<task>"`. The model plans, calls
 * tools, the CLI executes them (reads auto; mutating behind a permission gate)
 * and feeds results back; written files are verified against the repo's own
 * checks before the turn may finish. `deps` lets a parent (the REPL) share its
 * readline + abort so the loop runs embedded.
 */
export async function runAgent(
  task: string,
  opts: CliOptions,
  agent: AgentOptions,
  deps: { rl?: Interface; signal?: AbortSignal } = {},
): Promise<void> {
  const prompt = task.trim();
  if (!prompt) {
    error('usage: futurex agent "<task>"');
    process.exitCode = 1;
    return;
  }

  // Standalone: own the abort signal + SIGINT + readline. Embedded (REPL): the
  // caller passes its signal + readline so we never double-register or close them.
  const ownAc = deps.signal ? null : new AbortController();
  const signal: AbortSignal = deps.signal ?? (ownAc as AbortController).signal;
  const onSigint = (): void => ownAc?.abort();
  if (!deps.signal) process.on('SIGINT', onSigint);
  const ownRl = deps.rl === undefined;
  const rl = deps.rl ?? createInterface({ input: process.stdin, output: process.stdout });
  // With a readline attached to the TTY, Ctrl+C is delivered to the INTERFACE,
  // not the process — listen on both so the first Ctrl+C aborts cleanly.
  if (ownRl) rl.on('SIGINT', onSigint);
  const repoRoot = findRepoRoot();
  // Persistent per-project gate: store lookup first, prompt only when 'ask'.
  const gate = createPermissionGate(repoRoot, createReadlineAsk(rl), {
    autoApprove: agent.autoApprove,
  });

  try {
    const creds = readCreds();
    if (creds?.accessToken) {
      // Gateway: the backend runs the agent loop and asks this client to execute
      // tools (reads auto; writes/exec gated). No local AI or context engine.
      const tracker = createChangeTracker();
      const client = createGatewayClient(creds, {
        gate,
        repoRoot,
        onTool: (t) => tracker.observe(t),
        ...(opts.useRepo !== undefined ? { useRepo: opts.useRepo } : {}),
        ...(opts.useMemory !== undefined ? { useMemory: opts.useMemory } : {}),
      });
      info(`mode: gateway agent · ${opts.tier ?? 'fx-fast'}`);
      const history: Message[] = [{ role: 'user', content: prompt }];
      const result = await runGatewayTurnToCompletion({
        client,
        history,
        base: {
          model: opts.model,
          ...(opts.maxTokens !== undefined ? { maxTokens: opts.maxTokens } : {}),
          ...(opts.tier ? { tier: opts.tier } : {}),
          ...(opts.effort ? { effort: opts.effort } : {}),
        },
        signal,
        root: repoRoot,
        verify: agent.verify ?? 'auto',
        mutated: () => tracker.mutated(),
        changes: () => tracker.changes(),
        resetMutated: () => tracker.reset(),
      });
      if (result.error && !signal.aborted) {
        error(result.error);
        process.exitCode = 1;
      }
      return;
    }

    // Local fallback (direct DeepSeek loop) — needs FX_API_KEY.
    const ctx = loadContext(opts);
    const ai = createAiClient({ provider: 'deepseek' });
    const registry = createToolRegistry();
    const toolCtx: ToolContext = { repoRoot: ctx.root };
    const messages: ChatMessage[] = (
      await augmentMessages([{ role: 'user', content: prompt }] as Message[], ctx.retriever, opts.topK)
    ).map((m) => ({ role: m.role, content: m.content }));
    info(`mode: local agent · ${opts.tier ?? 'fx-fast'} · ${registry.specs.length} tools`);
    await runAgentTurn(messages, {
      ai,
      toolDefs: toolDefsFrom(registry),
      registry,
      toolCtx,
      agentSystem: `${ctx.system}\n\n${AGENT_PROTOCOL}`,
      model: opts.model,
      maxTokens: opts.maxTokens,
      maxSteps: agent.maxSteps,
      gate,
      signal,
      root: ctx.root,
      verify: agent.verify ?? 'auto',
    });
  } catch (err) {
    if (signal.aborted || (err instanceof Error && err.name === 'AbortError')) {
      info('cancelled');
    } else {
      error(err instanceof Error ? err.message : String(err));
      process.exitCode = 1;
    }
  } finally {
    if (ownRl) rl.close();
    if (!deps.signal) process.removeListener('SIGINT', onSigint);
  }
}

/**
 * A persistent agentic runtime for the REPL: holds the conversation history + the
 * tool loop, so every normal prompt can read/write files (gated) with multi-turn
 * memory. Shares the REPL's readline for the permission prompt.
 */
export interface ReplAgent {
  /** Run one user turn agentically (mutates internal history); returns final usage. */
  turn(userMessage: string, model: string, signal: AbortSignal): Promise<AiUsage | undefined>;
  /** Reset the conversation history. */
  clear(): void;
  /** Number of messages in the running history. */
  size(): number;
  /** Tool count (for the banner). */
  readonly toolCount: number;
}

export function createReplAgent(
  ctx: SessionContext,
  opts: CliOptions,
  agentOptions: AgentOptions,
  gate: Gate,
): ReplAgent {
  const ai = createAiClient({ provider: 'deepseek' });
  const registry = createToolRegistry();
  const toolCtx: ToolContext = { repoRoot: ctx.root };
  const toolDefs = toolDefsFrom(registry);
  const agentSystem = `${ctx.system}\n\n${AGENT_PROTOCOL}`;
  const messages: ChatMessage[] = [];

  return {
    toolCount: registry.specs.length,
    async turn(userMessage, model, signal) {
      const augmented = (
        await augmentMessages([{ role: 'user', content: userMessage }] as Message[], ctx.retriever, opts.topK)
      ).map((m) => ({ role: m.role, content: m.content }));
      messages.push(...augmented);
      return runAgentTurn(messages, {
        ai,
        toolDefs,
        registry,
        toolCtx,
        agentSystem,
        model,
        maxTokens: opts.maxTokens,
        maxSteps: agentOptions.maxSteps,
        gate,
        signal,
        root: ctx.root,
        verify: agentOptions.verify ?? 'auto',
      });
    },
    clear() {
      messages.length = 0;
    },
    size() {
      return messages.length;
    },
  };
}
